import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Nav } from '../icons/NavIcon';
import UserAvatar from './UserAvatar';

export default function UserProfileMenu({
  settingsPath = '/settings',
  subtitle,
  isCollapsed = false,
  onLogout,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayName = user?.name || 'User';

  return (
    <div ref={rootRef} className="relative mb-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`w-full rounded-lg text-left transition-colors hover:bg-black/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0071E3]/35 ${
          isCollapsed ? 'flex justify-center p-1' : 'flex items-center gap-3 p-1 -m-1'
        }`}
        title={isCollapsed ? `${displayName} — account menu` : undefined}
      >
        <UserAvatar user={user} size={40} />
        {!isCollapsed && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-text-primary truncate">{displayName}</p>
              {subtitle ? (
                <div className="text-[13px] text-txnweb-muted truncate leading-snug">{subtitle}</div>
              ) : null}
            </div>
            <Nav.chevronDown
              size={16}
              className={`shrink-0 text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-[60] min-w-[200px] rounded-xl border border-black/[0.08] bg-white shadow-lg py-1.5 ${
            isCollapsed ? 'left-full ml-2 bottom-0' : 'left-0 right-0 bottom-full mb-2'
          }`}
        >
          <Link
            role="menuitem"
            to={settingsPath}
            onClick={() => setOpen(false)}
            className="block px-3.5 py-2 text-sm text-text-primary hover:bg-black/[0.04]"
          >
            Profile
          </Link>
          <div className="my-1 border-t border-black/[0.06]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout?.();
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-text-secondary hover:text-accent-600 hover:bg-red-50/80"
          >
            <Nav.logOut size={16} aria-hidden />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
